import { motion } from "framer-motion";
import { ArrowRight, Briefcase, MapPin, Search } from "lucide-react";
import { useEffect, useState } from "react";
import Button from "../common/Button";
import { gsap } from "gsap";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";

import hero1 from "/images/hero-1.jpg";
import hero2 from "/images/hero-2.jpg";
import hero3 from "/images/hero-3.jpg";
import { useGSAP } from "@gsap/react";
import useFieldChoices from "../../hooks/useFieldChoices";
import Select from "../common/Select";
import { Grid, TextField, Button as MuiButton } from "@mui/material";

gsap.registerPlugin(MotionPathPlugin);

// Background slideshow
const backgroundImages = [hero1, hero2, hero3];

// Plane SVG curve (editable)
const planePath = "M 100 400 Q 400 100, 900 300";

const HeroSection = ({ onNavigate }) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const [searchFilters, setSearchFilters] = useState({
    query: "",
    location: "",
    pilot_type: "",
    total_flight_hours: "",
  });

  // const {fieldChoices} = useFieldChoices()

  // Background auto-rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImageIndex((prev) => (prev + 1) % backgroundImages.length);
    }, 7000);

    return () => clearInterval(interval);
  }, []);

  // GSAP floating clouds
  useGSAP(() => {
    gsap.to(".cloud-1", {
      x: 50,
      y: -20,
      duration: 21,
      repeat: -1,
      yoyo: true,
      ease: "power1.inOut",
    });

    gsap.to(".cloud-2", {
      x: -40,
      y: 25,
      duration: 25,
      repeat: -1,
      yoyo: true,
      ease: "power1.inOut",
    });
  }, []);

  // Plane animation
  useGSAP(() => {
    gsap.to(".hero-plane", {
      duration: 6,
      repeat: -1,
      ease: "power1.inOut",
      motionPath: {
        path: "#planePath",
        align: "#planePath",
        autoRotate: true,
        alignOrigin: [0.5, 0.5],
      },
    });
  }, []);

  const handleFilterChange = (field, value) => {
    setSearchFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();

    if (searchFilters.query) params.append("query", searchFilters.query);
    if (searchFilters.location)
      params.append("location", searchFilters.location);
    if (searchFilters.pilot_type)
      params.append("pilot_type", searchFilters.pilot_type);
    if (searchFilters.total_flight_hours)
      params.append("total_flight_hours", searchFilters.total_flight_hours);

    onNavigate(`/job-search?${params.toString()}`);
  };

  return (
    <section className="relative overflow-hidden bg-secondary-900 min-h-[90vh] flex items-center justify-center text-center">
      {/* ============================================================
                BACKGROUND SLIDESHOW + CINEMATIC ZOOM (NEW)
            ============================================================ */}
      <div className="absolute inset-0 z-0">
        {backgroundImages.map((img, index) => (
          <div
            key={index}
            className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ease-in-out bg-zoom ${
              index === activeImageIndex ? "opacity-60" : "opacity-0"
            }`}
            style={{ backgroundImage: `url(${img})` }}
          />
        ))}
      </div>

      {/* ============================================================
                CLOUD PARALLAX
            ============================================================ */}
      {/* <div className="absolute inset-0 z-10 pointer-events-none">
                <img src="/images/cloud-1.png" className="cloud cloud-1 absolute top-20 left-10 w-72 opacity-30" />
                <img src="/images/cloud-1.png" className="cloud cloud-2 absolute top-20 right-10 w-80 opacity-40" />
            </div> */}

      {/* Background Gradient */}
      <div className="absolute inset-0 z-20 bg-gradient-to-t from-secondary-900 via-secondary-900/70 to-transparent"></div>

      {/* ============================================================
                PLANE PATH + PLANE GSAP ANIMATION (NEW)
            ============================================================ */}
      <svg className="absolute top-0 left-0 w-full h-full z-[25] pointer-events-none">
        <path
          id="planePath"
          d={planePath}
          fill="none"
          stroke="white"
          strokeWidth="2"
          strokeDasharray="1000"
          strokeDashoffset="1000"
        />

        <image
          href="/images/plane-icon.png"
          width="40"
          height="40"
          className="hero-plane"
          style={{ transformOrigin: "center center" }}
        />
      </svg>

      {/* ============================================================
                HERO CONTENT — NOW USING FRAMER MOTION FOR PREMIUM FEEL
            ============================================================ */}
      <div className="container mx-auto px-6 py-16 relative z-[30]">
        <div className="max-w-4xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-4xl md:text-6xl font-extrabold text-white leading-tight mb-4"
          >
            Your Aviation Career
            <span className="block text-primary-400 mt-2">
              Takes Flight Here
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8, ease: "easeOut" }}
            className="text-lg md:text-xl text-secondary-200 max-w-2xl mx-auto leading-relaxed"
          >
            Connect with leading airlines and aerospace companies. Your next
            opportunity is just one search away.
          </motion.p>
          

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8, ease: "easeOut" }}
            className="mt-8 flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6"
          >
            <Button
              variant="primary"
              size="lg"
              onClick={() => onNavigate("/signup")}
            >
              Upload Your CV
            </Button>

            <Button
              variant="link"
              size="lg"
              className="text-white hover:text-primary-300 font-semibold"
              onClick={() => onNavigate("/signup?type=recruiter")}
              icon={<ArrowRight className="h-4 w-4" />}
              iconPosition="right"
            >
              I'm an Employer
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
